import { EnvironmentInjector, inject, Injectable, runInInjectionContext, signal } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { get, getDatabase, objectVal, onValue, ref, remove, runTransaction, update, off } from '@angular/fire/database';
import { Router } from '@angular/router';
import _ from 'lodash';
import { BehaviorSubject, Observable, Subscriber } from 'rxjs';
import { AllStatus } from '../../../core/services/status/all-status';
import { LanguageService } from '../../../core/services/language/language.service';
import { HttpClient } from '@angular/common/http';
import { BackendService } from '../../../core/services/backend/backend.service';
import { PlayersService } from '../players/players';
import { ICard, ICardResultState, ICardUserVariant } from '../../model/i-card';

interface ITurnAdvanceOptions {
  expectedPlayerId?: number;
  nextPlayerId?: number;
  otherCards?: any[];
}


@Injectable({
  providedIn: 'root'
})
export class RdGameService {
  private DATABASE_TABLE_NAME = 'games';
  private database;
  private injector: EnvironmentInjector = inject(EnvironmentInjector);
  posts$: Observable<Record<string, any>>;
  cards$ = new BehaviorSubject('');
  otherCards$ = new BehaviorSubject('');
  teamEvent$ = new BehaviorSubject('');
  game$: any;
  router = inject(Router);
  value$ = new BehaviorSubject<any>('');
  connected = signal(false);
  gameStateReady = signal(false);

  private watchedRoomUuid: string | null = null;
  private gameStateRevision = 0;
  private gameStateUnsubscribe?: () => void;
  private connectionUnsubscribe?: () => void;

  http = inject(HttpClient);
  backend = inject(BackendService);
  // players = inject(PlayersService);

  translate = inject(LanguageService);
  allStatus = inject(AllStatus);

  beginingEtap:any;

  constructor(){
    this.database = getDatabase(inject(FirebaseApp));
    this.posts$ = objectVal(ref(this.database, this.DATABASE_TABLE_NAME));
    // this.cards$ = objectVal(ref(this.database, this.DATABASE_TABLE_CARD));
  }

  createGame(game: any){
    const newGameKey = `${this.DATABASE_TABLE_NAME}/${game.uuid}`;
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.database, newGameKey), (currentGame) =>{
        if(!currentGame){
          return {
            ...game,
            players: this.mergeGamePlayers([], game.players),
          };
        }

        return {
          ...game,
          ...currentGame,
          players: this.mergeGamePlayers(currentGame.players, game.players),
        };
      })
    );
  }

  saveCard(card: ICard, ownerPlayerId?: number, expectedTurnId?: number): Promise<boolean>{
    const game = this.game$;
    if(!game?.uuid){
      return Promise.reject(new Error('Cannot save a card without an active room'));
    }
    const cardOwnerId = ownerPlayerId ?? game.turning_player_id;
    const turnId = expectedTurnId ?? Number(game.turn_id ?? 0);
    const gameKey = `${this.DATABASE_TABLE_NAME}/${game.uuid}`;
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.database, gameKey), (game) =>{
        if(!game ||
          String(game.turning_player_id) !== String(cardOwnerId) ||
          Number(game.turn_id ?? 0) !== Number(turnId)){
          return;
        }

        game.card_now = {
          ...card,
          owner_player_id: cardOwnerId,
          turn_id: turnId,
        };
        game.userResponse = null;
        game.previousPlayerScore = null;
        game.previousPlayerResultState = null;
        return game;
      })
    ).then(({ committed, snapshot }) =>{
      if(!committed){
        console.info(`Discarded stale card ${card.card_id} for room ${game.uuid}`);
      }else{
        this.publishGameState(snapshot.val());
      }
      return committed;
    });
  }

  addTeamEvent(list: any){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    return update(ref(this.database, gameKey), { team_event: list });
  }

  getCards(){
    if(this.game$){
      const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
      const startGameRef = ref(this.database,gameKey);
      runInInjectionContext(this.injector, () => {
        onValue(startGameRef, (game)=>{
          const gmInfo = game.val();

          if(gmInfo?.card_now){
            this.cards$.next(gmInfo.card_now);
            return;
          }

          this.cards$.next('');
        });
      });
    }else{
      this.goToStart();
    }
  }

  getOtherCards(){
    if(this.game$){
      const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
      const startGameRef = ref(this.database,gameKey);
      runInInjectionContext(this.injector, () => {
        onValue(startGameRef, (game)=>{
          const gmInfo = game.val();
          this.otherCards$.next( gmInfo?.other_cards );
        });
      });
      
    }else{
      this.goToStart();
    }
    
  }

  getTeamEvent(){
    if(this.game$){
      const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
      const startGameRef = ref(this.database,gameKey);
      runInInjectionContext(this.injector, () => {
        onValue(startGameRef, (game)=>{
          const gmInfo = game.val();
          this.teamEvent$.next( gmInfo?.team_event );
        });
      });
      
    }else{
      this.goToStart();
    }

  }

  deleteCard(): Promise<void>{
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}/card_now`;
    this.cards$.next('');
    return remove(ref(this.database, gameKey));
  }

  clearResponse(): Promise<void>{
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}/userResponse`;
    return remove(ref(this.database, gameKey));
  }

  deleteGameById(uuid: string){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    remove(ref(this.database, gameKey));
  }

  gameInfoById(uuid: string){
    let firstPages = true;
    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    runInInjectionContext(this.injector, () => {
      const startGameRef = ref(this.database,gameKey);
      onValue(startGameRef, (game)=>{
        try{
          const gameState = game.val();
          if(!gameState){
            this.game$ = null;
            return;
          }
          this.game$ = gameState;
          if(this.game$.stage_slug === "inprogress" && firstPages){
            this.runWay(this.game$.uuid, this.game$.maximum_players);
            firstPages = false;
          }
        }catch(error){
          this.goToStart();
        }
        
      });
    });
    
  }

  gameItSelf(uuid: string){
    this.stopWatchingGame();
    this.watchedRoomUuid = uuid;
    this.connected.set(false);
    this.gameStateReady.set(false);

    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    const startGameRef = ref(this.database,gameKey);
    runInInjectionContext(this.injector, () => {
      this.gameStateUnsubscribe = onValue(
        startGameRef,
        (game)=>{
          this.publishGameState(game.val());
        },
        (error)=>{
          this.connected.set(false);
          this.gameStateReady.set(false);
          console.error(`Failed to listen to game state for room ${uuid}`, error);
        }
      );

      this.connectionUnsubscribe = onValue(
        ref(this.database, '.info/connected'),
        (connection)=>{
          const isConnected = connection.val() === true;
          const wasConnected = this.connected();
          this.connected.set(isConnected);
          if(!isConnected){
            this.gameStateReady.set(false);
          }

          if(isConnected && !wasConnected){
            void this.refreshGameState(uuid);
          }
        },
        (error)=>{
          this.connected.set(false);
          this.gameStateReady.set(false);
          console.error('Failed to listen to Firebase connection state', error);
        }
      );
    });
  }

  stopWatchingGame(): void {
    this.gameStateUnsubscribe?.();
    this.connectionUnsubscribe?.();
    this.gameStateUnsubscribe = undefined;
    this.connectionUnsubscribe = undefined;
    this.watchedRoomUuid = null;
    this.connected.set(false);
    this.gameStateReady.set(false);
  }

  private publishGameState(game: any): void {
    this.gameStateRevision++;
    this.game$ = game;
    this.gameStateReady.set(game?.turning_player_id != null);
    this.value$.next(game);
  }

  private async refreshGameState(uuid: string): Promise<void> {
    const revisionAtRequest = this.gameStateRevision;

    try{
      const path = `${this.DATABASE_TABLE_NAME}/${uuid}`;
      const snapshot = await runInInjectionContext(this.injector, () =>
        get(ref(this.database, path))
      );
      if(this.watchedRoomUuid !== uuid || this.gameStateRevision !== revisionAtRequest){
        return;
      }

      this.publishGameState(snapshot.val());
    }catch(error){
      console.error(`Failed to refresh game state after reconnect for room ${uuid}`, error);
    }
  }

  updateDiceInGame(player: any){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    const index = this.game$.players.findIndex((item: any) => item.player_id === player.player_id);
    if(index < 0){
      return Promise.reject(new Error(`Player ${player.player_id} is not in room ${this.game$.uuid}`));
    }
    return update(ref(this.database, `${gameKey}/players/${index}`), {
      dice_value: player.dice_value,
      dice_sum: player.dice_sum,
    });
  }

  updateSuccessesInGame(player: any, value: number, points: number, maxPoints: number){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    const index = this.game$.players.findIndex((item: any) => item.player_id === player.player_id);
    if(index < 0){
      return Promise.reject(new Error(`Player ${player.player_id} is not in room ${this.game$.uuid}`));
    }
    return update(ref(this.database, `${gameKey}/players/${index}`), {
      successesInGame: value,
      points,
      maxPoints,
    });
  }



  updateGameById(uuid: string, room: any, player: any, players: any){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.database, gameKey), (game) =>{
        const currentGame = game || room;
        const roomPlayers = Array.isArray(players) ? players : [];

        return {
          ...currentGame,
          number_of_players: room.number_of_players,
          players: this.mergeGamePlayers(
            currentGame.players,
            [...roomPlayers, player],
          ),
        };
      })
    );
    
  }

  async updateGameByIdAvailable(uuid: string, room: any): Promise<void>{
    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    const snapshot = await runInInjectionContext(this.injector, () =>
      get(ref(this.database, gameKey))
    );
    if(snapshot.exists()){
      await runInInjectionContext(this.injector, () =>
        update(ref(this.database, gameKey), { available_rqs: room?.available_rqs ?? [] })
      );
    }
  }

  updatePlayerInGameById(player: any, uuid: string){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    const { rq_id, gender_icon, gender_id, gender_name, is_newbie, is_ready, name, quest_list } = player;
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.database, gameKey), (game) =>{
        if(!game){
          return;
        }

        const players = this.normalizeGamePlayers(game.players);
        const index = players.findIndex(
          (item: any) => String(item?.player_id) === String(player.player_id),
        );
        if(index < 0){
          return;
        }

        players[index] = {
          ...players[index],
          rq_id,
          gender_icon,
          gender_id,
          gender_name,
          is_newbie,
          is_ready,
          name,
          quest_list,
        };
        game.players = players;
        return game;
      })
    ).then(({ committed, snapshot }) =>{
      if(!snapshot.exists()){
        throw new Error(`Room ${uuid} does not exist`);
      }
      if(!committed){
        throw new Error(`Player ${player.player_id} is not in room ${uuid}`);
      }
      return snapshot.val();
    });
  }

  private mergeGamePlayers(currentPlayers: any, incomingPlayers: any[]): any[]{
    const mergedPlayers = this.normalizeGamePlayers(currentPlayers);

    for(const player of incomingPlayers){
      if(player?.player_id == null){
        continue;
      }

      const exists = mergedPlayers.some(
        (item: any) => String(item?.player_id) === String(player.player_id),
      );
      if(!exists){
        mergedPlayers.push(player);
      }
    }

    return mergedPlayers;
  }

  private normalizeGamePlayers(players: any): any[]{
    if(Array.isArray(players)){
      return players.filter((player: any) => player != null);
    }
    if(players && typeof players === 'object'){
      return Object.keys(players)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => players[key])
        .filter((player: any) => player != null);
    }
    return [];
  }
// TODO тут все переписати
  updateStatusGameInprogress(player_id: number, room: any){
    let updateData = {...room};
    if(updateData.available_rqs === undefined){
      delete updateData.available_rqs;
    }
    updateData.turn_id = Number.isInteger(updateData.turn_id) ? updateData.turn_id : 0;
    const gameKey = `${this.DATABASE_TABLE_NAME}/${updateData.uuid}`;
    updateData.turning_player_id = 	player_id;
    return runInInjectionContext(this.injector, () =>
      update(ref(this.database, gameKey), updateData)
    );
  }

  /**
   * Saves the submitted answer variants and score to the active game.
   * In co-op mode, also saves the result state so other players can show matching feedback.
   *
   * @param list Answer variants submitted by the player.
   * @param previousPlayerScore Player's score percentage; defaults to 0.
   * @param previousPlayerResultState Optional result category used for co-op feedback.
   * @returns A promise that resolves when the game update completes.
   */
  submitPlayerResponse(
    list: readonly ICardUserVariant[],
    previousPlayerScore: number,
    previousPlayerResultState?: ICardResultState,
  ){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    const response: {
      userResponse: ICardUserVariant[];
      previousPlayerScore: number;
      previousPlayerResultState?: ICardResultState;
    } = {
      userResponse: [...list],
      previousPlayerScore,
    };
    if(previousPlayerResultState){
      response.previousPlayerResultState = previousPlayerResultState;
    }
    return update(ref(this.database, gameKey), response);
  }

  updateOwn(own: Array<number>, player_id: number){
    const index = this.game$.players.findIndex((item: any) => item.player_id === player_id);
    if(index < 0){
      return Promise.reject(new Error(`Player ${player_id} is not in room ${this.game$.uuid}`));
    }
    const updates: Record<string, any> = {
      [`${this.DATABASE_TABLE_NAME}/${this.game$.uuid}/players/${index}/own`]: own,
    };
    if(_.isEmpty(own)){
      updates[`${this.DATABASE_TABLE_NAME}/${this.game$.uuid}/stopInACircle`] = true;
    }
    return update(ref(this.database), updates);
  }

  updateEndGame(val: boolean = true){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    return update(ref(this.database, gameKey), { endGame: val });
  }

  dataСollection(data: any, player: any): Promise<void>{
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}/dataCollection`;
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.database, gameKey), (collection) =>[
        ...(Array.isArray(collection) ? collection : []),
        { name: player.name, id: player.player_id, gIcon: player.gender_icon, data },
      ])
    ).then(()=> undefined);
  }

  updateStatusGameFinished(){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    return update(ref(this.database, gameKey), { stage_slug: 'finished', stage_id: 5 });
  }

  deleteUserwhithGame(player_id: number): Promise<void>{
    const uuid = this.game$?.uuid;
    if(!uuid){
      return Promise.reject(new Error('Cannot remove a player without an active room'));
    }

    const gameKey = `${this.DATABASE_TABLE_NAME}/${uuid}`;
    return runInInjectionContext(this.injector, () =>
      runTransaction(ref(this.database, gameKey), (game) =>{
        if(!game){
          return;
        }

        game.players = this.normalizeGamePlayers(game.players)
          .filter((player: any) => String(player?.player_id) !== String(player_id));

        if(Array.isArray(game.playersFields)){
          game.playersFields = game.playersFields
            .filter((player: any) => String(player?.playerId) !== String(player_id));
        }

        return game;
      })
    ).then(({ committed, snapshot }) =>{
      if(!snapshot.exists()){
        throw new Error(`Room ${uuid} does not exist`);
      }
      if(!committed){
        throw new Error(`Failed to remove player ${player_id} from room ${uuid}`);
      }
      this.publishGameState(snapshot.val());
    });
  }

  deleteUser(player_id: number){
    const gameKey = `${this.DATABASE_TABLE_NAME}/${this.game$.uuid}`;
    return update(ref(this.database, gameKey), {
      players: this.game$.players.filter((item: any)=> item.player_id !== player_id),
    });
  }

  runWay(uuid: string, maximum_players: number){
    const player = JSON.parse(sessionStorage.getItem('player') as string);
    if(player.is_ready){
      if(player.is_starter){
        // coop-game
        if(maximum_players > 1){
          this.router.navigate([`/coop-game/${uuid}`]);
        }else{
          this.router.navigate([`/single-game/${uuid}`]);
        }
        
      }else{
        const startingGameText = (this.translate.appLanguage === 'ru') ? 'Начало игры...' : (this.translate.appLanguage === 'uk') ? 'Початок гри...' : 'Starting game...';
        setTimeout(() => {
          this.allStatus.textSpinner = startingGameText;
          this.allStatus.setDisplayProgressSpinner(true);
          this.router.navigate([`/coop-game/${uuid}`]);
        });
      }
      
    }else{
      this.allStatus.textSpinner = (this.translate.appLanguage === 'ru') ? 'Выход из комнаты...' : (this.translate.appLanguage === 'uk') ? 'Вихід з кімнати...' : 'Leaving room...';
      this.allStatus.setDisplayProgressSpinner(true);
      //this.deletePlayerWithRoom(uuid, player.player_id);    
      this.deleteUser(player.player_id);
      off(ref(this.database));
      window.location.assign('/games');
      // this.router.navigate([`/step-one`]);
    }
    //this.beginingEtap();
  }

  

  goToStart(){
    off(ref(this.database,`${this.DATABASE_TABLE_NAME}`));
    this.router.navigate([`/step-one`]);
  }

  nextTurn(): Promise<void>{
    return this.advanceTurn();
  }

  async advanceTurn(
    uuid?: string,
    collection?: { data: any; player: any },
    options: ITurnAdvanceOptions = {},
  ): Promise<void>{
    const roomUuid = uuid ?? this.game$?.uuid;
    if(!roomUuid){
      throw new Error('Cannot advance turn without a room id');
    }

    const roomRef = ref(this.database, `${this.DATABASE_TABLE_NAME}/${roomUuid}`);
    const result = await runInInjectionContext(this.injector, () =>
      runTransaction(roomRef, (room) =>{
        if(!room ||
          (options.expectedPlayerId != null &&
            String(room.turning_player_id) !== String(options.expectedPlayerId))){
          return;
        }

        const nextPlayerId = options.nextPlayerId ?? this.getNextPlayerId(room);
        if((room.players || []).length &&
          !room.players.some((player: any) => String(player.player_id) === String(nextPlayerId))){
          return;
        }

        room.card_now = null;
        room.userResponse = null;
        room.previousPlayerScore = null;
        room.previousPlayerResultState = null;
        room.turning_player_id = nextPlayerId;
        room.turn_id = Number(room.turn_id ?? 0) + 1;
        room.other_cards = options.otherCards ?? [];
        room.other_cards_turn_id = room.turn_id;

        if(collection){
          room.dataCollection = [
            ...(Array.isArray(room.dataCollection) ? room.dataCollection : []),
            {
              name: collection.player.name,
              id: collection.player.player_id,
              gIcon: collection.player.gender_icon,
              data: collection.data,
            },
          ];
        }

        return room;
      })
    );

    if(!result.committed){
      throw new Error(`Turn for room ${roomUuid} changed before the transition completed`);
    }

    this.cards$.next('');
    this.publishGameState(result.snapshot.val());
  }

  private getNextPlayerId(room: any): number {
    const players = room?.players || [];
    const currentTurn = Number(room?.turning_player_id ?? 0);

    if(players.length <= 1){
      return currentTurn;
    }

    const nextPlayer = players
      .map((player: any) => ({ ...player, player_id: Number(player.player_id) }))
      .filter((player: any) => player.player_id > currentTurn)
      .sort((a: any, b: any) => a.player_id - b.player_id)[0];

    if(nextPlayer){
      return nextPlayer.player_id;
    }

    const starter = players.find((player: any) => player.is_starter);
    return Number(starter?.player_id ?? currentTurn);
  }

}
